/**
 * The paths both bundles have to agree on.
 *
 * This service ships two applications on one origin - the administration SPA under `/admin` and the
 * end-user SPA everywhere else - and they are built separately on purpose, so that an end user is never
 * sent the administrative screens. Two things still have to be spelled the same way in both, and this is
 * where they are spelled once:
 *
 * - **The sign-in page.** One page, one implementation, one session. It lives in the user bundle, which
 *   is what a visitor reaches first; the administration bundle's guard sends an unauthenticated reader
 *   here, and because the path is not one it owns, the router makes that a real page load.
 * - **Where the administration application starts.** The user bundle links to it for an account that
 *   holds Admin, and has to build the same URL the administration bundle's own routes are built from.
 *
 * It is in `platform/` rather than in either application because it is the seam between them, and a copy
 * in each is a copy that can disagree - the failure being a link into an application that renders its
 * catch-all on an origin where the page exists.
 */

/** The shared sign-in page. Served by the end-user bundle. */
export const LOGIN_PATH = '/login';

/** Where the administration application is mounted. */
export const ADMIN_PATH = '/admin';

/**
 * Where an operator signs in, which is inside the portal rather than beside it.
 *
 * The two applications used to share one sign-in page, because they shared an origin. They are being
 * split across two deployments - the developer application on the internet, the portal behind an
 * internal boundary - and a shared page cannot survive that: it would be served by the public host and
 * would put the operator's credential there. So each application signs in on its own.
 */
export const ADMIN_LOGIN_PATH = `${ADMIN_PATH}/login`;

/**
 * Where the developer application is served from.
 *
 * Empty in a single deployment, which is every checkout and the default configuration: the two
 * applications share an origin and a relative path is right. A split deployment sets it at build time to
 * the public host, because the portal has exactly one reason to name that origin - handing a borrowed
 * view across the boundary - and a wrong value there should be a broken link rather than a credential
 * sent somewhere unintended.
 */
export const PUBLIC_ORIGIN = (import.meta.env?.VITE_PUBLIC_ORIGIN ?? '').replace(/\/$/, '');

/**
 * The three generic screens the platform itself links to.
 *
 * They are administration screens by nature - one browses every sourcetype this service declares and the
 * other shows what one of them is made of - so they live in that bundle and carry its prefix. The
 * platform builds links to them from several places (the cell decorator, the records screen's toolbar,
 * every empty panel's way out), which is why they are here rather than spelled out at each call site: a prefix written
 * five times is a prefix that gets forgotten once.
 */
export const SOURCETYPES_PATH = `${ADMIN_PATH}/system/sourcetypes`;

export const RECORDS_PATH = `${ADMIN_PATH}/system/records`;

export const EXPLORE_PATH = `${ADMIN_PATH}/system/explore`;

/**
 * The operator directory.
 *
 * "Operators" rather than "users", because both of the other two populations here are also users of
 * something: a developer uses covdbg, and an account is a customer. These are the people who run the
 * service.
 */
export const OPERATORS_PATH = `${ADMIN_PATH}/system/operators`;

/**
 * A `next` value that can only be a page of this origin.
 *
 * `//host` and `https://host` are both things a browser will happily navigate to, so a redirect target
 * taken from the query string is an open redirect unless it is checked. One leading slash, and not two.
 *
 * It lives here because both bundles read `?next=`: the sign-in page to decide where to go afterwards,
 * and the administration bundle to build it in the first place.
 */
export function safeNext(value)
{
   const text = String(value ?? '');

   return text.startsWith('/') && !text.startsWith('//') ? text : null;
}
